import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdAccessTimeFilled, MdChecklist, MdRemoveRedEye, MdSearch } from "react-icons/md";
import Select from "react-select";

import empty_page from "../../../assets/selection_placeholder.svg";
import Button from "../../../components/Button";
import CustomDataTable from "../../../components/CustomDataTable";
import LoadingSpinner from "../../../components/LoadingSpinner";
import CodeSubmissionsModal from "../../../components/modals/common/CodeSubmissionsModal";
import ModifyingHistoryModal from "../../../components/modals/teacher/ModifyingHistoryModal";
import { LESSON_TYPES } from "../../../constants/common";
import { useTheme } from "../../../context/ThemeContext";
import classroomServices from "../../../services/classroomServices";
import teacherServices from "../../../services/teacherServices";
import { selectStylesV1 } from "../../../styles/selectStyles";
import { formatCustomDate } from "../../../utils/formatCustomDate";
import { getLanguage } from "../../../utils/getLanguage";

const TeacherCodeReviewByLesson = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const lang = getLanguage();

  // State management
  const [lessons, setLessons] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [selectedClassroom, setSelectedClassroom] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingClassrooms, setLoadingClassrooms] = useState(false);
  const [loadingLessons, setLoadingLessons] = useState(false);
  const [searchText, setSearchText] = useState("");

  const studentCodeSolutionsModalRef = useRef();
  const modifyingHistoryModalRef = useRef();

  const openModifyingHistoryModal = (studentName, selectedLesson, projectID) => {
    modifyingHistoryModalRef.current?.openModal(studentName, selectedLesson, projectID);
  };

  const openStudentCodeSolutionsModal = (
    lessonID,
    lessonsData,
    studentName,
    IDEType,
    index,
    selectedLesson
  ) => {
    studentCodeSolutionsModalRef.current?.openModal(
      lessonID,
      lessonsData,
      studentName,
      IDEType,
      index,
      selectedLesson
    );
  };

  const handleCodeSolutionsModalRefSuccess = () => {
    fetchStudents(selectedProgram?.value, selectedLesson?.value);
  };

  const fetchClassrooms = async () => {
    setLoadingClassrooms(true);
    try {
      const response = await teacherServices.getClassroomsByTeacher(null, {
        type: "other",
      });
      const formattedClassrooms = response.data.map((classroom) => ({
        label: classroom.name + " - " + classroom.section,
        value: classroom.id,
        course: classroom?.course?.course,
      }));

      setClassrooms(formattedClassrooms);
      const savedClassroomId = localStorage.getItem("selectedClassroomId");
      if (savedClassroomId) {
        const selected = formattedClassrooms.find((classroom) => classroom.value == savedClassroomId);
        setSelectedClassroom(selected || null);

        if (selected?.value) {
          handleSelectClassroom(selected);
        }
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error("Error fetching classrooms data:", error);
    } finally {
      setLoadingClassrooms(false);
    }
  };

  const fetchPrograms = () => {
    const translatedPrograms = LESSON_TYPES.map((item) => ({
      label: t(`${item.label}`),
      value: item.value,
    }));

    setPrograms(translatedPrograms);
    const savedProgramType = localStorage.getItem("selectedProgramType");
    if (savedProgramType) {
      const selected = translatedPrograms.find((program) => program.value == savedProgramType);

      setSelectedProgram(selected || null);

      if (selected?.value) {
        handleSelectProgram(selected);
      }
    }
  };

  const fetchLessons = async (classroomID, selectedProgram) => {
    setLoadingLessons(true);

    try {
      const res = await classroomServices.getClassroomLessons(classroomID, { type: selectedProgram });

      const formattedLessons = res.map((lesson) => ({
        ...lesson,
        label: lesson?.lesson?.title,
        value: lesson?.lesson?.id,
      }));
      setLessons(formattedLessons);
      // Restore selected lesson if available in local storage
      const savedLessonId = localStorage.getItem("selectedLessonId");

      if (savedLessonId) {
        const selected = formattedLessons.find((lesson) => lesson.value == savedLessonId);
        setSelectedLesson(selected || null);
        handleSelectLesson(selected);
      } else {
        setLoadingLessons(false);
      }
    } catch (error) {
      console.error("Error fetching classrooms:", error);
    } finally {
      setLoadingLessons(false);
    }
  };

  const fetchStudents = async (selectedProgram, lessonId) => {
    if (!selectedProgram) return;
    setLoading(true);
    try {
      const res = await classroomServices.getClassroomStudentsCodeCompletions(
        selectedClassroom?.value,
        lessonId,
        { type: selectedProgram }
      );

      setStudents(res.data);
      setFilteredStudents(res.data);
    } catch (error) {
      console.error("Error fetching students:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClassrooms();
    fetchPrograms();
  }, [lang]);

  useEffect(() => {
    if (selectedClassroom) {
      handleSelectProgram(selectedProgram);
      handleSelectLesson(selectedLesson);
    }
  }, [lang]);

  useEffect(() => {
    if (selectedClassroom?.value && selectedProgram?.value != "playground") {
      fetchLessons(selectedClassroom?.value, selectedProgram?.value);
    }
    if (selectedProgram?.value == "playground") {
      fetchStudents(selectedProgram?.value);
    }
  }, [selectedClassroom, selectedProgram]);

  useEffect(() => {
    return () => {
      localStorage.removeItem("selectedClassroomId");
      localStorage.removeItem("selectedProgramType");
      localStorage.removeItem("selectedLessonId");
    };
  }, []);

  const handleSelectClassroom = (option) => {
    setSelectedClassroom(option);

    if (option) {
      setSelectedProgram(null);
      fetchPrograms();
      setSelectedLesson(null);
    } else {
      setPrograms([]);
      setLessons([]);
      setSelectedLesson(null);
      setSelectedProgram(null);
    }
  };

  const handleSelectProgram = (option) => {
    setSelectedProgram(option);

    if (option) {
      setSelectedLesson(null);
    } else {
      setSelectedLesson(null);
      setSelectedProgram(null);
      setStudents([]);
    }
  };

  const handleSelectLesson = (option) => {
    setSelectedLesson(option);
    if (option) {
      fetchStudents(selectedProgram?.value, option?.value);
    } else {
      setStudents([]);
    }
  };
  const normalizeText = (text) => {
    return text.trim().replace(/\s+/g, " ").toLowerCase();
  };

  const handleSearch = (event) => {
    const rawInput = event.target.value;
    setSearchText(rawInput);

    const normalizedQuery = normalizeText(rawInput);

    const filtered = students.filter((student) =>
      normalizeText(student.student.name).includes(normalizedQuery)
    );

    setFilteredStudents(filtered);
  };

  const columnsLesson = [
    {
      name: t("student"),
      field: "name",
      sortable: true,
      body: (rowData, indexData) => (
        <Button
          onClick={() =>
            openStudentCodeSolutionsModal(
              rowData?.id,
              filteredStudents,
              rowData?.student?.name,
              rowData?.type,
              indexData?.rowIndex,
              selectedLesson
            )
          }
          variant="link"
          color="blue"
          label={rowData?.student?.name}
        />
      ),
    },
    {
      name: t("status"),
      field: "last_status",
      sortable: true,
    },
    {
      name: t("submit_date"),
      field: "date",
      sortable: true,
      body: (rowData) => <span className="font-medium">{formatCustomDate(rowData.date)}</span>,
    },

    {
      name: "",
      field: "id",
      body: (rowData, indexData) => (
        <div className="flex items-center gap-2">
          <Button
            tooltip={t("view_modifying_history")}
            onClick={() => openModifyingHistoryModal(rowData?.student?.name, rowData?.name, rowData?.id)}
            icon={<MdAccessTimeFilled size={22} className="text-blue-500" />}
            variant="text"
          />
          <Button
            onClick={() =>
              openStudentCodeSolutionsModal(
                rowData?.id,
                filteredStudents,
                rowData?.student?.name,
                rowData?.type,
                indexData?.rowIndex,
                selectedLesson
              )
            }
            icon={<MdRemoveRedEye size={22} className="text-blue-500" />}
            variant="text"
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="col-span-12 mb-3 grid grid-cols-1 gap-4 md:grid-cols-12">
        <div className="col-span-12 flex w-full items-center rounded-xl bg-white p-4 shadow-md dark:bg-dark-surface md:col-span-12 lg:col-span-4">
          <Select
            noOptionsMessage={() => <p>{t("no_options")}</p>}
            loadingMessage={() => <p>{t("loading")}</p>}
            className="w-full"
            isClearable
            isLoading={loadingClassrooms}
            options={classrooms}
            value={selectedClassroom}
            onChange={handleSelectClassroom}
            placeholder={t("select_classroom")}
            styles={selectStylesV1(theme)}
          />
        </div>
        {selectedClassroom && (
          <div
            className={`col-span-12 flex w-full items-center rounded-xl bg-white p-4 shadow-md dark:bg-dark-surface md:col-span-12 lg:col-span-4`}
          >
            <Select
              noOptionsMessage={() => <p>{t("no_options")}</p>}
              loadingMessage={() => <p>{t("loading")}</p>}
              className={"w-full"}
              isClearable
              options={programs}
              value={selectedProgram}
              onChange={handleSelectProgram}
              placeholder={t("select_program")}
              styles={selectStylesV1(theme)}
            />
          </div>
        )}
        {selectedClassroom && selectedProgram && selectedProgram.value !== "playground" && (
          <div
            className={`col-span-12 flex w-full items-center rounded-xl bg-white p-4 shadow-md dark:bg-dark-surface md:col-span-12 lg:col-span-4`}
          >
            <Select
              noOptionsMessage={() => <p>{t("no_options")}</p>}
              loadingMessage={() => <p>{t("loading")}</p>}
              className="w-full"
              isClearable
              options={lessons}
              isLoading={loadingLessons}
              value={selectedLesson}
              onChange={handleSelectLesson}
              placeholder={t("select_lesson")}
              styles={selectStylesV1(theme)}
            />
          </div>
        )}
      </div>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {(!selectedClassroom ||
            !selectedProgram ||
            (!selectedLesson && selectedProgram?.value !== "playground")) && (
            <div className="flex h-96 flex-col items-center justify-center space-y-9">
              <img height={"22%"} width={"22%"} className="opacity-75" src={empty_page} alt="" />
              <p className="text-lg font-medium text-gray-200">
                {t("select_classroom_program_and_lesson_to_view_students")}
              </p>
            </div>
          )}
          {selectedClassroom &&
            selectedProgram &&
            (selectedProgram?.value == "playground" || selectedLesson) && (
              <div className="col-span-12 rounded-xl bg-white shadow-md dark:bg-dark-surface">
                <CustomDataTable
                  loading={loading}
                  columns={columnsLesson}
                  data={filteredStudents}
                  withPagination
                  withSort
                  actions={
                    <span className="text-lg font-medium text-blue-400">{selectedClassroom?.course}</span>
                  }
                  search={
                    <div className="relative w-1/3">
                      <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-xl text-mutedText dark:text-dark-mutedText rtl:left-auto rtl:right-3" />
                      <input
                        type="text"
                        placeholder={t("search")}
                        value={searchText}
                        onChange={handleSearch}
                        className="w-full rounded-md border bg-white py-3 pl-10 pr-3 text-sm placeholder:text-mutedText focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-10 dark:border-dark-background dark:bg-dark-background dark:text-white rtl:pl-3 rtl:pr-10"
                      />
                    </div>
                  }
                />
              </div>
            )}
        </>
      )}
      <CodeSubmissionsModal
        ref={studentCodeSolutionsModalRef}
        onSubmit={handleCodeSolutionsModalRefSuccess}
        type="lesson"
        selectedProgram={selectedProgram?.value}
      />
      <ModifyingHistoryModal ref={modifyingHistoryModalRef} />
    </div>
  );
};

export default TeacherCodeReviewByLesson;
